/**
 * UAVTradeZone Modification Record
 *
 * Change ID:    MULTI_SERVICE_LOGS
 * Date:         2025-01-XX
 * Modified By:  Cursor
 *
 * Description:
 * Add API call to fetch logs from any service (server, mailer, uploader).
 *
 * Related Screens / Modules:
 * - client/src/services/adminLogs.ts
 * - client/src/features/admin/AdminLogsPage.tsx
 */

import axios from "axios";
import { apiUrl } from "../config/environment";
import {
  LogFile,
  LogListResponse,
  LogReadResponse,
  LogListParams,
  LogReadParams,
} from "../interfaces/Logs";
import { getAuthHeaders, getToken } from "../utils/authUtils";

// [2025-01-XX] Multi-service logs: Service type definition
export type ServiceType = "server" | "mailer" | "uploader";
export type LogType = "combined" | "error";

// [2025-01-XX] Multi-service logs: Log entry interface
export interface LogEntry {
  level: string;
  message: string;
  timestamp: string;
  service: string;
  raw?: string;
  [key: string]: any; // [2025-01-XX] Additional context fields
}

// [2025-01-XX] Multi-service logs: API response interface
export interface ServiceLogsResponse {
  success: boolean;
  data: {
    logs: LogEntry[];
    service: ServiceType;
    type: LogType;
    total: number;
  };
}

/**
 * List all log files with optional pagination and filtering
 * @param params - Query parameters for filtering and pagination
 * @returns Array of LogFile objects
 */
export const listLogs = async (params?: LogListParams): Promise<LogFile[]> => {
  const { data } = await axios.get<LogListResponse>(`${apiUrl}/api/admin/logs`, {
    params: params || {},
    ...getAuthHeaders(),
  });
  return data.data.files;
};

/**
 * Read log file content (tail mode by default)
 * @param filename - Name of the log file
 * @param params - Query parameters (lines, raw)
 * @returns Array of log lines as strings
 */
export const readLog = async (filename: string, params?: LogReadParams): Promise<string[]> => {
  const { data } = await axios.get<LogReadResponse>(
    `${apiUrl}/api/admin/logs/${encodeURIComponent(filename)}`,
    {
      params: params || { lines: 200 },
      ...getAuthHeaders(),
    },
  );

  // Normalize response: server returns 'content' array
  const content = data.data.content || [];

  // Convert to string array (handle both string and object entries)
  return content.map((entry) => {
    if (typeof entry === "string") {
      return entry;
    } else if (entry && typeof entry === "object") {
      // If it's a parsed object, try to extract raw or message
      if (entry.raw) {
        return entry.raw;
      }
      // Otherwise stringify the object
      return JSON.stringify(entry);
    }
    return String(entry);
  });
};

/**
 * Tail last N lines from a log file (optimized endpoint)
 * @param filename - Name of the log file
 * @param params - Query parameters (lines)
 * @returns Array of log lines as strings
 */
export const tailLog = async (filename: string, params?: LogReadParams): Promise<string[]> => {
  const { data } = await axios.get<LogReadResponse>(
    `${apiUrl}/api/admin/logs/${encodeURIComponent(filename)}/tail`,
    {
      params: params || { lines: 200 },
      ...getAuthHeaders(),
    },
  );

  // Normalize response
  const content = data.data.content || [];

  return content.map((entry) => {
    if (typeof entry === "string") {
      return entry;
    } else if (entry && typeof entry === "object") {
      if (entry.raw) {
        return entry.raw;
      }
      return JSON.stringify(entry);
    }
    return String(entry);
  });
};

/**
 * Get download URL for a log file
 * @param filename - Name of the log file
 * @returns Direct download URL
 */
export const downloadLogUrl = (filename: string): string => {
  return `${apiUrl}/api/admin/logs/${encodeURIComponent(filename)}/download`;
};

/**
 * Download a log file (triggers browser download)
 * @param filename - Name of the log file
 */
export const downloadLog = (filename: string): void => {
  // [2026-07-08] cookie-auth-fix: cookie authenticates; no localStorage token required
  const token = getToken();

  // Create a temporary link and click it to trigger download
  const url = downloadLogUrl(filename);
  const link = document.createElement("a");
  link.href = url;
  link.download = filename;

  // Add auth header via fetch for proper authorization
  fetch(url, {
    // [2026-07-08] cookie-auth-fix: send cookie with fetch (axios defaults don't apply to fetch)
    credentials: "include",
    headers: {
      Authorization: token || "",
    },
  })
    .then((response) => response.blob())
    .then((blob) => {
      const blobUrl = window.URL.createObjectURL(blob);
      link.href = blobUrl;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(blobUrl);
    })
    .catch((error) => {
      console.error("Download error:", error);
      throw error;
    });
};

const base = `${apiUrl}/api/mail/requests`;

export interface MailRequestItem {
  _id: string;
  type: string;
  to: string[];
  userEmail?: string | null;
  replyTo?: string | null;
  subject?: string | null;
  template: string;
  payload: any;
  status: "queued" | "processing" | "sent" | "failed" | "cancelled";
  attempts: number;
  lastError?: { code?: string | null; message?: string | null } | null;
  providerMeta?: any;
  createdAt: string;
  updatedAt: string;
}

export interface MailRequestsQuery {
  status?: string;
  type?: string;
  to?: string;
  from?: string;
  toDate?: string;
  page?: number;
  limit?: number;
  sort?: string | string[];
}

export async function getMailRequests(params: MailRequestsQuery = {}) {
  const token = getToken();
  const res = await axios.get(`${base}`, {
    params,
    headers: { Authorization: token || "" },
  });
  return res.data?.data || { items: [], meta: { page: 1, pageSize: 20, total: 0 } };
}

export async function getMailRequestById(id: string) {
  const token = getToken();
  const res = await axios.get(`${base}/${id}`, {
    headers: { Authorization: token || "" },
  });
  return res.data?.data as MailRequestItem;
}

// [2025-01-XX] Multi-service logs: Fetch logs from any service
export async function getServiceLogs(
  service: ServiceType,
  type: LogType = "combined",
  search: string = "",
  limit: number = 100,
): Promise<ServiceLogsResponse> {
  const response = await axios.get<ServiceLogsResponse>(`${apiUrl}/api/admin/logs/service`, {
    params: { service, type, search, limit },
    ...getAuthHeaders(),
  });
  return response.data;
}
// [2026-07-08] redeploy: re-commit cookie-auth fix to trigger rebuild
